import { sharedStorage } from 'src/common/storage/storage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { ElevatorStatus, StatusModel } from '../models/StatusModel';
import { PAGE_SIZE } from '../constants';

export interface Group {
  id: string;
  userId: string;
  name: string;
}

export interface ErisStatusResultsModel {
  pageElements: StatusModel[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface ErisSummaryModel {
  groupSummaries: ErisSummaryResultsModel[];
  total: ErisSummaryResultsModel;
}

export interface ErisSummaryResultsModel {
  groupName: string;
  units: number;
  recoveredUnits: number;
  heliosConfirmedUnits: number;
  repairRequiredUnits: number;
  suspendedUnits: number;
  recoveryRate: number;
  rescuedUnits: number;
  confinedUnits: number;
}

export interface ErisSearchParameters {
  groupName?: string | null;
  userId?: string | null;
  buildingName?: string;
  operationStatus?: ElevatorStatus | undefined;
}

export interface ErisStatusSearchingParameters {
  searchParams: ErisSearchParameters;
  pageNumber: number;
  pageSize: number;
}

export interface Disaster {
  id: number;
  startDate: number | null;
  stopDate: number | null;
  lastUpdateDate: number;
}

export interface ErisInformationStore {
  availableGroups: Group[];
  selectedGroup: Group | null;
  isSearchingResults: boolean;
  statusResult: ErisStatusResultsModel | null;
  statusSearchingParameters: ErisStatusSearchingParameters;
  statusSummaryList: ErisSummaryModel | null;
  latestDisasterUpdate: Disaster | null;
  previousDisasterUpdate: Disaster | null;
  setAvailableGroups: (availableGroups: Group[]) => void;
  setSelectedGroup: (selectedGroup: Group | null) => void;
  setIsSearchingResults: (isSearchingResults: boolean) => void;
  setStatusResult: (statusResult: ErisStatusResultsModel | null) => void;
  setStatusSummaryList: (statusSummaryList: ErisSummaryModel | null) => void;
  patchStatusPagination: (patch: {
    searchParams?: Partial<ErisSearchParameters>;
    pageNumber?: number;
    pageSize?: number;
  }) => void;
  setLatestDisasterUpdate: (latestDisasterUpdate: Disaster | null) => void;
  setPreviousDisasterUpdate: (previousDisasterUpdate: Disaster | null) => void;
  resetAllInformation: () => void;
}

export const useErisInformationStore = create<ErisInformationStore>()(
  persist<ErisInformationStore>(
    (set) => ({
      availableGroups: [],
      selectedGroup: null,
      isSearchingResults: false,
      statusResult: null,
      statusSearchingParameters: {
        searchParams: {
          buildingName: '',
          groupName: null,
          userId: null,
          operationStatus: undefined,
        },
        pageNumber: 0,
        pageSize: PAGE_SIZE,
      },
      statusSummaryList: null,
      latestDisasterUpdate: null,
      previousDisasterUpdate: null,

      setAvailableGroups: (availableGroups) => set({ availableGroups }),
      setSelectedGroup: (group) => {
        set(() => ({
          selectedGroup: group,
          statusResult: null,
          statusSummaryList: null,
          statusSearchingParameters: {
            searchParams: {
              groupName: group?.name ?? null,
              userId: group?.userId ?? null,
              buildingName: '',
              operationStatus: undefined,
            },
            pageNumber: 0,
            pageSize: PAGE_SIZE,
          },
        }));
      },
      setIsSearchingResults: (isSearchingResults) => set({ isSearchingResults }),
      setStatusResult: (result) =>
        set((state) => {
          const { pageNumber, pageSize } = state.statusSearchingParameters;
          const nextPage = result?.pageNumber ?? pageNumber;
          const nextSize = result?.pageSize ?? pageSize;

          const params =
            nextPage === pageNumber && nextSize === pageSize
              ? state.statusSearchingParameters
              : { ...state.statusSearchingParameters, pageNumber: nextPage, pageSize: nextSize };

          return { statusResult: result, statusSearchingParameters: params };
        }),
      setStatusSummaryList: (statusSummaryList) => set({ statusSummaryList }),

      patchStatusPagination: (patch) => {
        set((state) => ({
          statusSearchingParameters: {
            ...state.statusSearchingParameters,
            ...(patch.pageNumber != null && { pageNumber: patch.pageNumber }),
            ...(patch.pageSize != null && { pageSize: patch.pageSize }),
            searchParams: {
              ...state.statusSearchingParameters.searchParams,
              ...patch.searchParams,
            },
          },
        }));
      },
      setLatestDisasterUpdate: (latestDisasterUpdate) => set({ latestDisasterUpdate }),
      setPreviousDisasterUpdate: (previousDisasterUpdate) => set({ previousDisasterUpdate }),
      resetAllInformation: () =>
        set({
          availableGroups: [],
          selectedGroup: null,
          isSearchingResults: false,
          statusResult: null,
          statusSearchingParameters: {
            searchParams: {
              buildingName: '',
              groupName: null,
              operationStatus: undefined,
              userId: null,
            },
            pageNumber: 0,
            pageSize: PAGE_SIZE,
          },
          statusSummaryList: null,
          latestDisasterUpdate: null,
          previousDisasterUpdate: null,
        }),
    }),
    { name: 'er-information-store', storage: createJSONStorage(() => sharedStorage) }
  )
);
